import { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Divider, InputNumber, Select, Skeleton, Space, Switch, Typography } from 'antd';
import { BellOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';

export interface NotificationAlertsConfig {
  listenerOnline: { enabled: boolean; cooldownSec: number };
  liveStarted: { enabled: boolean; cooldownSec: number };
  saved: { dailyCap: number; minGapMin: number };
  general: { nightStartHour: number; nightEndHour: number; nightCap: number; dayCap: number; minGapMin: number };
}

interface AdminNotificationAlertsView extends NotificationAlertsConfig {
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Same bounds the API enforces (shared-types ALERT_SETTING_BOUNDS). */
export const ALERT_BOUNDS = {
  cap: { min: 0, max: 50 },
  minGapMin: { min: 0, max: 1440 },
  cooldownSec: { min: 0, max: 3600 },
} as const;

const QUERY_KEY = ['admin', 'notification-alerts'];

const pick = (v: AdminNotificationAlertsView): NotificationAlertsConfig => ({
  listenerOnline: { ...v.listenerOnline },
  liveStarted: { ...v.liveStarted },
  saved: { ...v.saved },
  general: { ...v.general },
});

/** "21" → "9 PM". */
export function hourLabel(h: number): string {
  const suffix = h < 12 ? 'AM' : 'PM';
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve} ${suffix}`;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({ value: h, label: hourLabel(h) }));

function serverError(e: unknown): string {
  const data = (e as { response?: { data?: { message?: string } } }).response?.data;
  return data?.message ?? 'Could not save notification settings';
}

function NumberField(props: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <Typography.Text>{props.label}</Typography.Text>
      <div>
        <InputNumber
          aria-label={props.label}
          min={props.min}
          max={props.max}
          precision={0}
          value={props.value}
          disabled={props.disabled}
          onChange={(v) => props.onChange(v ?? 0)}
        />
      </div>
      {props.hint && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {props.hint}
        </Typography.Text>
      )}
    </div>
  );
}

/**
 * Settings → App configuration → Push notifications. Who hears about a buddy
 * coming online / going live, and how often:
 *
 *   - Saved buddies: users who saved a buddy hear when it comes online or goes
 *     live, any hour, up to a daily cap with a minimum gap.
 *   - Everyone else: live alerts only, with one cap for the night window and
 *     another for the rest of the day, plus a minimum gap.
 *
 * The API applies changes within about a minute; no deploy.
 */
export function NotificationAlertsCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('notification_alerts.edit');
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => (await api.get<AdminNotificationAlertsView>('/admin/notifications/alerts')).data,
  });
  const [draft, setDraft] = useState<NotificationAlertsConfig | null>(null);

  useEffect(() => {
    if (data && draft === null) setDraft(pick(data));
  }, [data, draft]);

  const save = useMutation({
    mutationFn: async (body: NotificationAlertsConfig) =>
      (await api.post<AdminNotificationAlertsView>('/admin/notifications/alerts', body)).data,
    onSuccess: (saved) => {
      message.success('Notification settings saved');
      qc.setQueryData(QUERY_KEY, saved);
      setDraft(pick(saved));
    },
    onError: (e) => message.error(serverError(e)),
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load notification settings"
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => void refetch()}>
            Retry
          </Button>
        }
      />
    );
  }
  if (!draft) return null;

  const set = <K extends keyof NotificationAlertsConfig>(section: K, patch: Partial<NotificationAlertsConfig[K]>) =>
    setDraft({ ...draft, [section]: { ...draft[section], ...patch } });
  const dirty = JSON.stringify(draft) !== JSON.stringify(pick(data));
  const g = draft.general;
  const noNight = g.nightStartHour === g.nightEndHour;
  const disabled = !canEdit;

  return (
    <Card
      title={
        <Space>
          <BellOutlined />
          Push notifications
        </Space>
      }
      extra={
        data.updatedAt ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Last changed {new Date(data.updatedAt).toLocaleString('en-IN')}
          </Typography.Text>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 620 }}>
        <Typography.Text type="secondary">
          "Buddy is online" goes only to users who saved that buddy. "Buddy is live" goes to everyone: users who
          saved the buddy use the saved-buddy limits, everyone else the limits below. All times are IST. A
          user who turned off "Buddy online" in the app, or is on a call, is never pushed.
        </Typography.Text>

        <Space>
          <Switch
            aria-label="Buddy is online alerts"
            checked={draft.listenerOnline.enabled}
            disabled={disabled}
            onChange={(v) => set('listenerOnline', { enabled: v })}
          />
          <Typography.Text>"Buddy is online" alerts (saved buddies)</Typography.Text>
        </Space>
        <Space>
          <Switch
            aria-label="Buddy is live alerts"
            checked={draft.liveStarted.enabled}
            disabled={disabled}
            onChange={(v) => set('liveStarted', { enabled: v })}
          />
          <Typography.Text>"Buddy is live" alerts</Typography.Text>
        </Space>

        <Divider orientation="left" plain style={{ margin: 0 }}>
          Saved buddies — any time of day
        </Divider>
        <NumberField
          label="Max per user per day"
          hint={draft.saved.dailyCap === 0 ? 'None will be sent.' : 'Online and live alerts together.'}
          value={draft.saved.dailyCap}
          {...ALERT_BOUNDS.cap}
          disabled={disabled}
          onChange={(v) => set('saved', { dailyCap: v })}
        />
        <NumberField
          label="Saved: minimum gap (minutes)"
          hint="Between two alerts to the same user. 0 = no gap."
          value={draft.saved.minGapMin}
          {...ALERT_BOUNDS.minGapMin}
          disabled={disabled}
          onChange={(v) => set('saved', { minGapMin: v })}
        />

        <Divider orientation="left" plain style={{ margin: 0 }}>
          Everyone else — live alerts only
        </Divider>
        <div>
          <Typography.Text>Night window</Typography.Text>
          <div>
            <Space>
              <Select
                aria-label="Night window start"
                style={{ width: 110 }}
                options={HOUR_OPTIONS}
                value={g.nightStartHour}
                disabled={disabled}
                onChange={(v: number) => set('general', { nightStartHour: v })}
              />
              <Typography.Text>to</Typography.Text>
              <Select
                aria-label="Night window end"
                style={{ width: 110 }}
                options={HOUR_OPTIONS}
                value={g.nightEndHour}
                disabled={disabled}
                onChange={(v: number) => set('general', { nightEndHour: v })}
              />
            </Space>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {noNight
              ? 'Start and end are the same — no night window; the daytime limit applies all day.'
              : `${hourLabel(g.nightStartHour)} up to ${hourLabel(g.nightEndHour)}.`}
          </Typography.Text>
        </div>
        <NumberField
          label="Max per user in the night window"
          hint={g.nightCap === 0 ? 'None will be sent.' : undefined}
          value={g.nightCap}
          {...ALERT_BOUNDS.cap}
          disabled={disabled || noNight}
          onChange={(v) => set('general', { nightCap: v })}
        />
        <NumberField
          label="Max per user during the day"
          hint={g.dayCap === 0 ? 'None will be sent.' : 'Outside the night window, per day.'}
          value={g.dayCap}
          {...ALERT_BOUNDS.cap}
          disabled={disabled}
          onChange={(v) => set('general', { dayCap: v })}
        />
        <NumberField
          label="Everyone else: minimum gap (minutes)"
          hint="Between two alerts to the same user. 0 = no gap."
          value={g.minGapMin}
          {...ALERT_BOUNDS.minGapMin}
          disabled={disabled}
          onChange={(v) => set('general', { minGapMin: v })}
        />

        <Divider orientation="left" plain style={{ margin: 0 }}>
          Duplicate guard
        </Divider>
        <Space size="large" wrap>
          <NumberField
            label="Online: per buddy (seconds)"
            value={draft.listenerOnline.cooldownSec}
            {...ALERT_BOUNDS.cooldownSec}
            disabled={disabled}
            onChange={(v) => set('listenerOnline', { cooldownSec: v })}
          />
          <NumberField
            label="Live: per stream (seconds)"
            value={draft.liveStarted.cooldownSec}
            {...ALERT_BOUNDS.cooldownSec}
            disabled={disabled}
            onChange={(v) => set('liveStarted', { cooldownSec: v })}
          />
        </Space>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Stops a retry or a second device from sending the same alert twice. Not a rate limit — keep it small.
        </Typography.Text>

        {canEdit && (
          <Space>
            <Button
              type="primary"
              data-testid="notification-alerts-save"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              Save
            </Button>
            {dirty && (
              <Button onClick={() => setDraft(pick(data))} disabled={save.isPending}>
                Discard
              </Button>
            )}
          </Space>
        )}
      </Space>
    </Card>
  );
}
